import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Mail, Phone, Mic, Plus, Trash2, ExternalLink, Settings as SettingsIcon, RefreshCw, CheckCircle, XCircle, Volume2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommunicationSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [newSMTP, setNewSMTP] = useState({
    name: "",
    email: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    imap_host: "",
    imap_port: 993,
    use_tls: true,
  });

  const [twilioSettings, setTwilioSettings] = useState({
    account_sid: "",
    auth_token: "",
    phone_number: "",
    whatsapp_number: "",
  });
  
  // OpenAI Realtime settings
  const [openaiRealtimeSettings, setOpenaiRealtimeSettings] = useState({
    model: "gpt-4o-realtime-preview",
  });
  const [availableRealtimeModels, setAvailableRealtimeModels] = useState([]);
  const [testingOpenaiRealtime, setTestingOpenaiRealtime] = useState(false);
  const [openaiRealtimeTestResult, setOpenaiRealtimeTestResult] = useState(null);

  const [elevenlabsSettings, setElevenlabsSettings] = useState({
    api_key: "",
    default_voice_id: "",
  });

  const [testingTwilio, setTestingTwilio] = useState(false);
  const [twilioTestStatus, setTwilioTestStatus] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("token");
    try {
      const [smtpRes, twilioRes, elevenlabsRes, openaiRealtimeRes] = await Promise.all([
        axios.get(`${API}/settings/communication/smtp`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/communication/twilio`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/communication/elevenlabs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/communication/openai-realtime`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setSmtpConfigs(smtpRes.data);
      setTwilioSettings(twilioRes.data);
      setElevenlabsSettings(elevenlabsRes.data);
      setOpenaiRealtimeSettings({ model: openaiRealtimeRes.data.model });
      setAvailableRealtimeModels(openaiRealtimeRes.data.available_models || []);
    } catch (error) {
      toast.error("Failed to fetch communication settings");
    }
  };
  
  const handleSaveOpenaiRealtime = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${API}/settings/communication/openai-realtime`,
        openaiRealtimeSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("OpenAI Realtime settings saved successfully");
    } catch (error) {
      toast.error("Failed to save OpenAI Realtime settings");
    } finally {
      setLoading(false);
    }
  };
  
  const handleTestOpenaiRealtime = async () => {
    setTestingOpenaiRealtime(true);
    setOpenaiRealtimeTestResult(null);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${API}/settings/communication/test-openai-realtime`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpenaiRealtimeTestResult(response.data);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to test OpenAI Realtime connection");
      setOpenaiRealtimeTestResult({ success: false, message: "Connection test failed" });
    } finally {
      setTestingOpenaiRealtime(false);
    }
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      // Editing existing config
      setEditingConfig(config);
      setNewSMTP({
        name: config.name,
        email: config.email,
        smtp_host: config.smtp_host,
        smtp_port: config.smtp_port,
        smtp_username: config.smtp_username,
        smtp_password: config.smtp_password,
        imap_host: config.imap_host || "",
        imap_port: config.imap_port || 993,
        use_tls: config.use_tls,
      });
    } else {
      // Adding new config
      setEditingConfig(null);
      setNewSMTP({
        name: "",
        email: "",
        smtp_host: "",
        smtp_port: 587,
        smtp_username: "",
        smtp_password: "",
        imap_host: "",
        imap_port: 993,
        use_tls: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSaveSMTP = async () => {
    if (!newSMTP.name || !newSMTP.email || !newSMTP.smtp_host || !newSMTP.smtp_username || !newSMTP.smtp_password) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      if (editingConfig) {
        // Update existing config
        await axios.put(`${API}/settings/communication/smtp/${editingConfig.id}`, newSMTP, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("SMTP configuration updated successfully");
      } else {
        // Add new config
        await axios.post(`${API}/settings/communication/smtp`, newSMTP, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("SMTP configuration added successfully");
      }
      
      setDialogOpen(false);
      setEditingConfig(null);
      setNewSMTP({
        name: "",
        email: "",
        smtp_host: "",
        smtp_port: 587,
        smtp_username: "",
        smtp_password: "",
        imap_host: "",
        imap_port: 993,
        use_tls: true,
      });
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save SMTP configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSMTP = async (configId) => {
    if (!confirm("Are you sure you want to delete this SMTP configuration?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/settings/communication/smtp/${configId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("SMTP configuration deleted");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to delete SMTP configuration");
    }
  };

  const handleTestTwilio = async () => {
    if (!twilioSettings.account_sid || !twilioSettings.auth_token) {
      toast.error("Please enter Account SID and Auth Token");
      return;
    }

    setTestingTwilio(true);
    setTwilioTestStatus(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/settings/communication/test-twilio`,
        {
          account_sid: twilioSettings.account_sid,
          auth_token: twilioSettings.auth_token,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setTwilioTestStatus({ 
        success: true, 
        message: response.data.message,
        accountName: response.data.account_name,
        accountStatus: response.data.account_status
      });
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Connection failed";
      setTwilioTestStatus({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingTwilio(false);
    }
  };

  const handleUpdateTwilio = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/settings/communication/twilio`, twilioSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Twilio settings updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update Twilio settings");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateElevenlabs = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/settings/communication/elevenlabs`, elevenlabsSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("ElevenLabs settings updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update ElevenLabs settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="communication-settings-page">
      <div className="max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Communication Settings</h1>
          <p className="text-slate-600">Configure your communication channels and integrations</p>
        </div>

        <Tabs defaultValue="smtp" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-3xl">
            <TabsTrigger value="smtp" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              SMTP/IMAP
            </TabsTrigger>
            <TabsTrigger value="twilio" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Twilio
            </TabsTrigger>
            <TabsTrigger value="elevenlabs" className="flex items-center gap-2">
              <Mic className="w-4 h-4" />
              ElevenLabs
            </TabsTrigger>
            <TabsTrigger value="openai-realtime" className="flex items-center gap-2" data-testid="openai-realtime-tab">
              <Volume2 className="w-4 h-4" />
              OpenAI Voice
            </TabsTrigger>
          </TabsList>

          {/* SMTP/IMAP Tab */}
          <TabsContent value="smtp">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      SMTP/IMAP Configurations
                    </CardTitle>
                    <CardDescription>Manage your email server configurations</CardDescription>
                  </div>
                  <Button 
                    className="bg-indigo-600 hover:bg-indigo-700" 
                    onClick={() => navigate("/settings/communication/email/new")}
                    data-testid="add-smtp-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Email Configuration
                  </Button>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen} style={{display: 'none'}}>
                    <DialogTrigger asChild>
                      <Button style={{display: 'none'}}>Hidden</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{editingConfig ? "Edit" : "Add"} SMTP/IMAP Configuration</DialogTitle>
                        <DialogDescription>
                          {editingConfig ? "Update your" : "Configure a new"} email server connection
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Name *</Label>
                            <Input
                              id="name"
                              value={newSMTP.name}
                              onChange={(e) => setNewSMTP({ ...newSMTP, name: e.target.value })}
                              placeholder="Ben Thomas"
                              data-testid="smtp-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="smtp-email">Email *</Label>
                            <Input
                              id="smtp-email"
                              type="email"
                              value={newSMTP.email}
                              onChange={(e) => setNewSMTP({ ...newSMTP, email: e.target.value })}
                              placeholder="benison.thomas@shoppias.com"
                              data-testid="smtp-email-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="smtp_host">SMTP Host *</Label>
                          <Input
                            id="smtp_host"
                            value={newSMTP.smtp_host}
                            onChange={(e) => setNewSMTP({ ...newSMTP, smtp_host: e.target.value })}
                            placeholder="smtp.gmail.com"
                            data-testid="smtp-host-input"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="smtp_port">SMTP Port *</Label>
                            <Input
                              id="smtp_port"
                              type="number"
                              value={newSMTP.smtp_port}
                              onChange={(e) => setNewSMTP({ ...newSMTP, smtp_port: parseInt(e.target.value) })}
                              placeholder="587"
                              data-testid="smtp-port-input"
                            />
                          </div>
                          <div className="flex items-center space-x-2 pt-8">
                            <Switch
                              id="use_tls"
                              checked={newSMTP.use_tls}
                              onCheckedChange={(checked) => setNewSMTP({ ...newSMTP, use_tls: checked })}
                              data-testid="smtp-tls-switch"
                            />
                            <Label htmlFor="use_tls">Use TLS</Label>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="smtp_username">SMTP Username *</Label>
                          <Input
                            id="smtp_username"
                            value={newSMTP.smtp_username}
                            onChange={(e) => setNewSMTP({ ...newSMTP, smtp_username: e.target.value })}
                            placeholder="your-email@example.com"
                            data-testid="smtp-username-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="smtp_password">SMTP Password *</Label>
                          <Input
                            id="smtp_password"
                            type="password"
                            value={newSMTP.smtp_password}
                            onChange={(e) => setNewSMTP({ ...newSMTP, smtp_password: e.target.value })}
                            placeholder="Your app password"
                            data-testid="smtp-password-input"
                          />
                        </div>

                        <div className="border-t pt-4 mt-4">
                          <h4 className="font-medium mb-4 text-slate-700">IMAP Settings (Optional)</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="imap_host">IMAP Host</Label>
                              <Input
                                id="imap_host"
                                value={newSMTP.imap_host}
                                onChange={(e) => setNewSMTP({ ...newSMTP, imap_host: e.target.value })}
                                placeholder="imap.gmail.com"
                                data-testid="imap-host-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="imap_port">IMAP Port</Label>
                              <Input
                                id="imap_port"
                                type="number"
                                value={newSMTP.imap_port}
                                onChange={(e) => setNewSMTP({ ...newSMTP, imap_port: parseInt(e.target.value) })}
                                placeholder="993"
                                data-testid="imap-port-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">
                          Cancel
                        </Button>
                        <Button onClick={handleSaveSMTP} disabled={loading} data-testid="save-smtp-button">
                          {loading ? (editingConfig ? "Updating..." : "Adding...") : (editingConfig ? "Update Configuration" : "Add Configuration")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {smtpConfigs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No SMTP configurations yet. Add your first configuration to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {smtpConfigs.map((config) => (
                      <div
                        key={config.id}
                        className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                        data-testid="smtp-config-item"
                      >
                        <div>
                          <h4 className="font-semibold text-slate-800">{config.name}</h4>
                          <p className="text-sm text-slate-600">{config.email}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {config.smtp_host}:{config.smtp_port}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/settings/communication/email/${config.id}`)}
                            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                            data-testid="configure-smtp-button"
                            title="Configure"
                          >
                            <SettingsIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteSMTP(config.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-smtp-button"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Twilio Tab */}
          <TabsContent value="twilio">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Twilio Configuration
                </CardTitle>
                <CardDescription>
                  Configure Twilio for SMS, WhatsApp, and voice calls.
                  <a
                    href="https://www.twilio.com/docs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 ml-2 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    View Documentation <ExternalLink className="w-3 h-3" />
                  </a>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateTwilio} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="account_sid">Account SID</Label>
                    <Input
                      id="account_sid"
                      value={twilioSettings.account_sid}
                      onChange={(e) => setTwilioSettings({ ...twilioSettings, account_sid: e.target.value })}
                      placeholder="AC..."
                      className="h-11 border-slate-300 font-mono text-sm"
                      data-testid="twilio-sid-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="auth_token">Auth Token</Label>
                    <Input
                      id="auth_token"
                      type="password"
                      value={twilioSettings.auth_token}
                      onChange={(e) => setTwilioSettings({ ...twilioSettings, auth_token: e.target.value })}
                      placeholder="Your auth token"
                      className="h-11 border-slate-300 font-mono text-sm"
                      data-testid="twilio-token-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone_number">Phone Number (SMS/Calls)</Label>
                      <Input
                        id="phone_number"
                        value={twilioSettings.phone_number}
                        onChange={(e) => setTwilioSettings({ ...twilioSettings, phone_number: e.target.value })}
                        placeholder="+1234567890"
                        className="h-11 border-slate-300"
                        data-testid="twilio-phone-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="whatsapp_number">WhatsApp Number</Label>
                      <Input
                        id="whatsapp_number"
                        value={twilioSettings.whatsapp_number}
                        onChange={(e) => setTwilioSettings({ ...twilioSettings, whatsapp_number: e.target.value })}
                        placeholder="+1234567890"
                        className="h-11 border-slate-300"
                        data-testid="twilio-whatsapp-input"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <Button
                      type="button"
                      onClick={handleTestTwilio}
                      disabled={testingTwilio || !twilioSettings.account_sid || !twilioSettings.auth_token}
                      className="w-full mb-4"
                      variant="outline"
                      data-testid="test-twilio-button"
                    >
                      {testingTwilio ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Testing Connection...
                        </>
                      ) : (
                        <>
                          <Phone className="w-4 h-4 mr-2" />
                          Test Twilio Connection
                        </>
                      )}
                    </Button>
                    
                    {twilioTestStatus && (
                      <div className={`flex items-start gap-2 p-3 rounded mb-4 ${twilioTestStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        {twilioTestStatus.success ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${twilioTestStatus.success ? 'text-green-900' : 'text-red-900'}`}>
                            {twilioTestStatus.message}
                          </p>
                          {twilioTestStatus.success && twilioTestStatus.accountName && (
                            <div className="mt-2 text-xs text-green-800">
                              <p><strong>Account:</strong> {twilioTestStatus.accountName}</p>
                              <p><strong>Status:</strong> {twilioTestStatus.accountStatus}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-6"
                      data-testid="save-twilio-button"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ElevenLabs Tab */}
          <TabsContent value="elevenlabs">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mic className="w-5 h-5" />
                      ElevenLabs Configuration
                    </CardTitle>
                    <CardDescription>
                      Configure ElevenLabs for text-to-speech, voice cloning, dubbing, and more
                      <a
                        href="https://elevenlabs.io/docs/quickstart"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 ml-2 text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        View Documentation <ExternalLink className="w-3 h-3" />
                      </a>
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => navigate("/settings/communication/elevenlabs")}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <SettingsIcon className="w-4 h-4 mr-2" />
                    Configure All Features
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateElevenlabs} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="elevenlabs_api_key">API Key</Label>
                    <Input
                      id="elevenlabs_api_key"
                      type="password"
                      value={elevenlabsSettings.api_key}
                      onChange={(e) => setElevenlabsSettings({ ...elevenlabsSettings, api_key: e.target.value })}
                      placeholder="Your ElevenLabs API key"
                      className="h-11 border-slate-300 font-mono text-sm"
                      data-testid="elevenlabs-api-key-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Get your API key from{" "}
                      <a
                        href="https://elevenlabs.io/app/settings/api-keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        ElevenLabs Dashboard
                      </a>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default_voice_id">Default Voice ID</Label>
                    <Input
                      id="default_voice_id"
                      value={elevenlabsSettings.default_voice_id}
                      onChange={(e) => setElevenlabsSettings({ ...elevenlabsSettings, default_voice_id: e.target.value })}
                      placeholder="21m00Tcm4TlvDq8ikWAM"
                      className="h-11 border-slate-300 font-mono text-sm"
                      data-testid="elevenlabs-voice-id-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Find voice IDs in your{" "}
                      <a
                        href="https://elevenlabs.io/app/voice-library"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        Voice Library
                      </a>
                    </p>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-6"
                      data-testid="save-elevenlabs-button"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* OpenAI Realtime Voice Tab */}
          <TabsContent value="openai-realtime">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-green-600" />
                  OpenAI Realtime Voice API
                </CardTitle>
                <CardDescription>
                  Configure the OpenAI Realtime model for voice conversations. This model is used for the "Start a Call" feature on public actor pages.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveOpenaiRealtime} className="space-y-6">
                  {/* Info Alert */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Volume2 className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">About OpenAI Realtime API</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          The Realtime API enables real-time voice conversations with AI. Make sure your OpenAI API key has access to the Realtime models.
                          You can check your access in the{" "}
                          <a
                            href="https://platform.openai.com/account/limits"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline font-medium"
                          >
                            OpenAI Dashboard
                          </a>.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Model Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="realtime_model">Realtime Model</Label>
                    <Select
                      value={openaiRealtimeSettings.model}
                      onValueChange={(value) => setOpenaiRealtimeSettings({ ...openaiRealtimeSettings, model: value })}
                    >
                      <SelectTrigger className="h-11 border-slate-300" data-testid="realtime-model-select">
                        <SelectValue placeholder="Select a model" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRealtimeModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{model.name}</span>
                              <span className="text-xs text-slate-500">{model.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Select the OpenAI Realtime model to use for voice conversations. "Latest" is recommended for best compatibility.
                    </p>
                  </div>

                  {/* Test Connection */}
                  <div className="space-y-2">
                    <Label>Test Connection</Label>
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleTestOpenaiRealtime}
                        disabled={testingOpenaiRealtime}
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        data-testid="test-openai-realtime-button"
                      >
                        {testingOpenaiRealtime ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Test OpenAI Realtime Connection
                          </>
                        )}
                      </Button>
                      {openaiRealtimeTestResult && (
                        <div className={`flex items-center gap-2 text-sm ${openaiRealtimeTestResult.success ? "text-green-600" : "text-red-600"}`}>
                          {openaiRealtimeTestResult.success ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          <span>{openaiRealtimeTestResult.message}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      This will test the connection to OpenAI Realtime API using your configured OpenAI API key from API Keys settings.
                    </p>
                  </div>

                  {/* Current Model Info */}
                  {openaiRealtimeSettings.model && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h4 className="font-medium text-slate-800 mb-2">Currently Selected Model</h4>
                      <code className="text-sm bg-slate-200 px-2 py-1 rounded">{openaiRealtimeSettings.model}</code>
                    </div>
                  )}

                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6"
                      data-testid="save-openai-realtime-button"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}